import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";

/**
 * Nested Product Architecture Matrix (matches gemini tree design).
 * Product cards → indented groups → item variant chips.
 * Rename available on products, groups, and items.
 */
export default function ProductHierarchyBoard() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [productForm, setProductForm] = useState({ code: "", name: "" });
  const [openForm, setOpenForm] = useState(null); // `product:{id}` | `group:{id}` | `rename:{kind}:{id}`
  const [nestForm, setNestForm] = useState({ code: "", name: "" });
  const [renameForm, setRenameForm] = useState({ code: "", name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/production/masters-hierarchy", { params: { include_inactive: 1 } });
      setTree(data.results || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nextSort = (rows = []) =>
    (rows.reduce((m, r) => Math.max(m, Number(r.sort_order) || 0), 0) || 0) + 10;

  const withBusy = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      setOpenForm(null);
      setNestForm({ code: "", name: "" });
      setRenameForm({ code: "", name: "" });
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleForm = (key) => {
    setOpenForm((cur) => (cur === key ? null : key));
    setNestForm({ code: "", name: "" });
  };

  const startRename = (kind, row) => {
    setOpenForm(`rename:${kind}:${row.id}`);
    setRenameForm({ code: row.code || "", name: row.name || "" });
  };

  const createProduct = async (e) => {
    e.preventDefault();
    const code = productForm.code.trim();
    const name = productForm.name.trim();
    if (!code || !name) return toast.error("Provide code and name");
    await withBusy(async () => {
      await api.post("/production/masters/products", {
        code,
        name,
        sort_order: nextSort(tree),
      });
      setProductForm({ code: "", name: "" });
    }, "Product line created");
  };

  const createGroup = async (product) => {
    const code = nestForm.code.trim();
    const name = nestForm.name.trim();
    if (!code || !name) return toast.error("Provide group code and name");
    await withBusy(async () => {
      await api.post("/production/masters/item-groups", {
        code,
        name,
        sort_order: nextSort(product.groups || []),
        product_id: product.id,
      });
    }, "Sub-group added");
  };

  const createItem = async (group) => {
    const code = nestForm.code.trim();
    const name = nestForm.name.trim();
    if (!code || !name) return toast.error("Provide item code and name");
    await withBusy(async () => {
      await api.post("/production/masters/items", {
        code,
        name,
        sort_order: nextSort(group.items || []),
        item_group_id: group.id,
      });
    }, "Item variant added");
  };

  const saveRename = async (kind, id) => {
    const name = renameForm.name.trim();
    const code = renameForm.code.trim();
    if (!name || !code) return toast.error("Code and name are required");
    await withBusy(async () => {
      await api.patch(`/production/masters/${kind}/${id}`, { name, code });
    }, "Renamed");
  };

  const removeNode = async (kind, row) => {
    const msgs = {
      products: `Remove product line “${row.name}”? All sub-groups and items under it will be deleted.`,
      "item-groups": `Remove group “${row.name}”? All item variants under it will be deleted.`,
      items: `Remove item variant “${row.name}”?`,
    };
    if (!window.confirm(msgs[kind] || `Remove “${row.name}”?`)) return;
    await withBusy(async () => {
      await api.delete(`/production/masters/${kind}/${row.id}`);
    }, "Removed");
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-[#e2dbcd] bg-[#f9f8f3] px-6 py-16 text-center text-sm text-[#717672]">
        Loading product architecture…
      </div>
    );
  }

  return (
    <div className="max-w-[1000px]" data-testid="product-hierarchy-board" style={{ color: "#2d312e" }}>
      <header className="mb-6 border-b border-[#e2dbcd] pb-4">
        <h3 className="m-0 text-[26px] font-semibold" style={{ color: "#556B2F" }}>
          Product Architecture Matrix
        </h3>
        <p className="mt-2 mb-0 text-sm text-[#717672]">
          A clear, nested view of your manufacturing outputs. Add, remove, or edit custom variants inline.
        </p>
      </header>

      {/* Root product form */}
      <form
        onSubmit={createProduct}
        className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-[#e2dbcd] bg-card p-4"
      >
        <span className="text-sm font-semibold whitespace-nowrap">+ New Product Strain:</span>
        <div className="flex flex-col sm:flex-row flex-1 gap-2 min-w-0">
          <input
            value={productForm.code}
            onChange={(e) => setProductForm((f) => ({ ...f, code: e.target.value }))}
            placeholder="Code (e.g., RICE)"
            disabled={busy}
            className="h-9 w-full sm:w-40 rounded-md border border-[#e2dbcd] px-3 text-[13px] outline-none focus:border-[#556B2F]"
          />
          <input
            value={productForm.name}
            onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Name (e.g., Rice)"
            disabled={busy}
            className="h-9 w-full sm:w-40 rounded-md border border-[#e2dbcd] px-3 text-[13px] outline-none focus:border-[#556B2F]"
          />
          <button
            type="submit"
            disabled={busy}
            className="h-9 rounded-md bg-[#556B2F] px-3.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            Create Product Line
          </button>
        </div>
      </form>

      {tree.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#e2dbcd] bg-card px-6 py-12 text-center text-sm text-[#888]">
          No operational products currently defined.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {tree.map((product) => {
            const productFormKey = `product:${product.id}`;
            const renamingProduct = openForm === `rename:products:${product.id}`;
            const groups = product.groups || [];

            return (
              <article
                key={product.id}
                className="overflow-hidden rounded-lg border border-[#e2dbcd] bg-card shadow-[0_1px_3px_rgba(0,0,0,0.02)]"
              >
                {/* Product row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#e2dbcd] px-5 py-4">
                  <div className="flex flex-wrap items-baseline gap-2.5 min-w-0">
                    {renamingProduct ? (
                      <RenameInline
                        form={renameForm}
                        setForm={setRenameForm}
                        busy={busy}
                        onSave={() => saveRename("products", product.id)}
                        onCancel={() => setOpenForm(null)}
                      />
                    ) : (
                      <>
                        <span className="text-lg font-semibold" style={{ color: "#556B2F" }}>
                          {product.name}
                        </span>
                        <CodeBadge code={product.code} />
                        <Badge strong>PRIMARY PRODUCT</Badge>
                        {!product.is_active ? <Badge muted>OFF</Badge> : null}
                      </>
                    )}
                  </div>
                  {!renamingProduct ? (
                    <div className="flex flex-wrap items-center gap-3 shrink-0">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => toggleForm(productFormKey)}
                        className="rounded border border-solid border-[#556B2F] px-2 py-0.5 text-xs font-semibold text-[#556B2F] hover:bg-[#e9efe0] disabled:opacity-50"
                      >
                        + Add Sub-Group
                      </button>
                      <ActionLink onClick={() => startRename("products", product)} disabled={busy}>
                        Rename
                      </ActionLink>
                      <ActionLink danger onClick={() => removeNode("products", product)} disabled={busy}>
                        Remove Line
                      </ActionLink>
                    </div>
                  ) : null}
                </div>

                <InlineFormPanel open={openForm === productFormKey}>
                  <div className="mx-5 my-4">
                    <NestAddForm
                      codePh="Group Code"
                      namePh="Group Name"
                      submitLabel="Add Group Category"
                      form={nestForm}
                      setForm={setNestForm}
                      busy={busy}
                      onSubmit={() => createGroup(product)}
                      onCancel={() => setOpenForm(null)}
                    />
                  </div>
                </InlineFormPanel>

                {/* Groups */}
                {groups.length === 0 && openForm !== productFormKey ? (
                  <p className="px-5 py-4 text-sm text-[#888] m-0">No sub-groups yet.</p>
                ) : (
                  groups.map((group) => {
                    const groupFormKey = `group:${group.id}`;
                    const renamingGroup = openForm === `rename:item-groups:${group.id}`;
                    const items = group.items || [];

                    return (
                      <div
                        key={group.id}
                        className="relative border-b border-[#edebe4] bg-[#FAF9F5] px-5 py-4 pl-10 last:border-b-0"
                      >
                        <span
                          aria-hidden
                          className="absolute left-5 top-0 bottom-0 w-0.5 bg-[#e2dbcd]"
                        />

                        <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex flex-wrap items-baseline gap-2.5 min-w-0">
                            {renamingGroup ? (
                              <RenameInline
                                form={renameForm}
                                setForm={setRenameForm}
                                busy={busy}
                                onSave={() => saveRename("item-groups", group.id)}
                                onCancel={() => setOpenForm(null)}
                              />
                            ) : (
                              <>
                                <span className="text-sm font-semibold text-[#111]">{group.name}</span>
                                <CodeBadge code={group.code} />
                                <Badge>GROUP</Badge>
                                {!group.is_active ? <Badge muted>OFF</Badge> : null}
                              </>
                            )}
                          </div>
                          {!renamingGroup ? (
                            <div className="flex items-center gap-3 shrink-0">
                              <ActionLink onClick={() => startRename("item-groups", group)} disabled={busy}>
                                Rename
                              </ActionLink>
                              <ActionLink danger onClick={() => removeNode("item-groups", group)} disabled={busy}>
                                Remove
                              </ActionLink>
                            </div>
                          ) : null}
                        </div>

                        {/* Items */}
                        <div className="ml-6 flex flex-wrap items-center gap-2.5 rounded-md border border-[#e2dbcd] bg-card px-3.5 py-3">
                          {items.map((item) => {
                            const renamingItem = openForm === `rename:items:${item.id}`;
                            if (renamingItem) {
                              return (
                                <div key={item.id} className="w-full sm:w-auto">
                                  <RenameInline
                                    form={renameForm}
                                    setForm={setRenameForm}
                                    busy={busy}
                                    onSave={() => saveRename("items", item.id)}
                                    onCancel={() => setOpenForm(null)}
                                  />
                                </div>
                              );
                            }
                            return (
                              <span
                                key={item.id}
                                className={`inline-flex items-center gap-2 rounded-full border border-[#e2dbcd] bg-[#f4f6f0] px-3 py-1.5 text-[13px] ${
                                  item.is_active ? "" : "opacity-60"
                                }`}
                              >
                                <span>
                                  <strong className="font-semibold text-[#2d312e]">{item.name}</strong>{" "}
                                  <CodeBadge code={item.code} />
                                </span>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => startRename("items", item)}
                                  className="text-[11px] text-[#717672] underline hover:text-foreground bg-transparent border-0 p-0 cursor-pointer disabled:opacity-40"
                                  title="Rename item variant"
                                >
                                  Rename
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => removeNode("items", item)}
                                  className="text-[#c94c4c] font-bold text-base leading-none hover:text-red-600 bg-transparent border-0 p-0 cursor-pointer disabled:opacity-40"
                                  aria-label={`Remove ${item.name}`}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}

                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => toggleForm(groupFormKey)}
                            className="rounded border border-dashed border-[#556B2F] px-2 py-0.5 text-xs font-semibold text-[#556B2F] hover:bg-[#e9efe0] disabled:opacity-50"
                          >
                            + Add Item Variant
                          </button>

                          <InlineFormPanel open={openForm === groupFormKey} fullWidth>
                            <div className="w-full basis-full mt-1">
                              <NestAddForm
                                codePh="Item Code"
                                namePh="Item Name"
                                submitLabel="Save"
                                form={nestForm}
                                setForm={setNestForm}
                                busy={busy}
                                onSubmit={() => createItem(group)}
                                onCancel={() => setOpenForm(null)}
                              />
                            </div>
                          </InlineFormPanel>
                        </div>
                      </div>
                    );
                  })
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CodeBadge({ code }) {
  return (
    <span className="inline-flex items-center rounded bg-[#eee] px-1.5 py-px font-mono text-[11px] text-[#717672]">
      {code}
    </span>
  );
}

function Badge({ children, strong, muted }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold ${
        muted
          ? "bg-[#ecece8] text-[#888]"
          : strong
            ? "bg-[#e0ebd5] text-[#556B2F]"
            : "bg-[#e9efe0] text-[#556B2F]"
      }`}
    >
      {children}
    </span>
  );
}

function ActionLink({ children, onClick, disabled, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`bg-transparent border-0 p-0 text-xs underline cursor-pointer disabled:opacity-40 ${
        danger ? "text-[#c94c4c] hover:text-red-600" : "text-[#717672] hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function InlineFormPanel({ open, children, fullWidth }) {
  return (
    <div
      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
        fullWidth ? "w-full basis-full" : ""
      } ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
    >
      <div className="overflow-hidden min-h-0">{children}</div>
    </div>
  );
}

function NestAddForm({ codePh, namePh, submitLabel, form, setForm, busy, onSubmit, onCancel }) {
  const codeRef = useRef(null);
  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  return (
    <form
      className="flex flex-col sm:flex-row flex-wrap gap-2 rounded-md border border-[#e2dbcd] bg-card p-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <input
        ref={codeRef}
        value={form.code}
        onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
        placeholder={codePh}
        disabled={busy}
        className="h-9 w-full sm:w-40 rounded-md border border-[#e2dbcd] px-3 text-[13px] outline-none focus:border-[#556B2F]"
      />
      <input
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder={namePh}
        disabled={busy}
        className="h-9 w-full sm:w-40 rounded-md border border-[#e2dbcd] px-3 text-[13px] outline-none focus:border-[#556B2F]"
      />
      <button
        type="submit"
        disabled={busy}
        className="h-9 rounded-md bg-[#556B2F] px-3.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
      >
        {submitLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="h-9 rounded-md border border-[#e2dbcd] px-3 text-[13px] text-[#717672] hover:bg-[#f9f8f3]"
      >
        Cancel
      </button>
    </form>
  );
}

function RenameInline({ form, setForm, busy, onSave, onCancel }) {
  const nameRef = useRef(null);
  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, []);

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <input
        ref={nameRef}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        disabled={busy}
        className="h-8 min-w-[7rem] rounded border border-[#556B2F] px-2 text-sm font-semibold outline-none"
        placeholder="Name"
      />
      <input
        value={form.code}
        onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
        disabled={busy}
        className="h-8 w-28 rounded border border-[#e2dbcd] px-2 font-mono text-xs uppercase outline-none focus:border-[#556B2F]"
        placeholder="Code"
      />
      <button
        type="submit"
        disabled={busy}
        className="h-8 rounded bg-[#556B2F] px-2.5 text-[11px] font-semibold text-white disabled:opacity-50"
      >
        Save
      </button>
      <button type="button" onClick={onCancel} className="h-8 px-1 text-xs text-[#717672]">
        Cancel
      </button>
    </form>
  );
}
